import { CheckoutSection } from 'server/types/pmts-overrides';
import {
  IcoPlus,
  IcoTrash,
  IcoRocketLaunch,
  IcoTag,
  IcoDocument,
  IcoExclamation,
  IcoCalendar,
  IcoCheckBadge,
  IcoQuestion,
  IcoCreditCard,
  IcoUserCircle,
  IcoUsers,
  IcoVideoCamera,
  IcoLock,
  IcoCog,
  IcoChartBar,
  IcoCursor,
} from '@components/icons';

export const SECTION_TYPE_META: Record<
  string,
  { icon: (props: any) => preact.VNode; desc: string }
> = {
  hero: { icon: IcoRocketLaunch, desc: 'Full-width banner with headline' },
  eyebrow: { icon: IcoTag, desc: 'Small label text above content' },
  content: { icon: IcoDocument, desc: 'Rich text paragraphs and headings' },
  callout: { icon: IcoExclamation, desc: 'Highlighted message box' },
  schedule: { icon: IcoCalendar, desc: 'Day-by-day event schedule' },
  valueStack: { icon: IcoCheckBadge, desc: 'Bullet list of benefits' },
  featureGrid: { icon: IcoChartBar, desc: 'Multi-column feature grid' },
  faq: { icon: IcoQuestion, desc: 'Expandable Q&A section' },
  integratedCheckout: { icon: IcoCreditCard, desc: 'Payment form with Stripe' },
  checkoutForm: { icon: IcoCreditCard, desc: 'Standalone checkout form' },
  instructor: { icon: IcoUserCircle, desc: 'Instructor bio and photo' },
  testimonials: { icon: IcoUsers, desc: 'Customer quotes and reviews' },
  video: { icon: IcoVideoCamera, desc: 'Embedded video player' },
  trust: { icon: IcoLock, desc: 'Security and guarantee badges' },
  urgency: { icon: IcoExclamation, desc: 'Scarcity or deadline bar' },
  footer: { icon: IcoCog, desc: 'Page footer with links' },
  cta: { icon: IcoCursor, desc: 'Accept / decline buttons' },
};

type Props = {
  section: CheckoutSection;
  onChange: (section: CheckoutSection) => void;
  priceBadgePlaceholder?: string;
};

function Field({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <label class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      placeholder={placeholder}
      class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
      placeholder={placeholder}
      rows={rows}
      class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
    />
  );
}

function ColorInput({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <label class="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        class="size-8 rounded border border-gray-300 cursor-pointer"
      />
      <span class="text-sm text-gray-600">{label}</span>
      <input
        type="text"
        value={value}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        class="border border-gray-300 rounded px-2 py-1 text-xs w-24 font-mono"
      />
    </label>
  );
}

function RepeatableItems<T>({
  items,
  onChange,
  renderItem,
  createItem,
  addLabel,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  renderItem: (item: T, index: number, update: (item: T) => void) => preact.ComponentChildren;
  createItem: () => T;
  addLabel: string;
}) {
  function updateItem(index: number, updated: T) {
    const newItems = [...items];
    newItems[index] = updated;
    onChange(newItems);
  }

  function removeItem(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function addItem() {
    onChange([...items, createItem()]);
  }

  return (
    <div class="flex flex-col gap-3">
      {items.map((item, i) => (
        <div key={i} class="flex gap-2 items-start">
          <div class="flex-1">{renderItem(item, i, (updated) => updateItem(i, updated))}</div>
          <button
            type="button"
            onClick={() => removeItem(i)}
            class="p-1.5 text-gray-400 hover:text-red-500 mt-1"
          >
            <IcoTrash class="size-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        class="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800"
      >
        <IcoPlus class="size-3.5" />
        {addLabel}
      </button>
    </div>
  );
}

export function SectionEditor({ section, onChange, priceBadgePlaceholder }: Props) {
  switch (section.type) {
    case 'hero':
      return (
        <div class="flex flex-col gap-4">
          {!section.content.headline && (
            <div class="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <IcoExclamation class="size-3.5 flex-shrink-0" />
              Headline is required for this section to display properly.
            </div>
          )}
          <Field label="Headline">
            <TextInput
              value={section.content.headline}
              onChange={(headline) =>
                onChange({ ...section, content: { ...section.content, headline } })
              }
              placeholder="Your headline here"
            />
          </Field>
          <Field label="Subtitle">
            <TextInput
              value={section.content.subtitle || ''}
              onChange={(subtitle) =>
                onChange({ ...section, content: { ...section.content, subtitle } })
              }
              placeholder="Optional subtitle"
            />
          </Field>
          <Field label="Subtitle Line 2">
            <TextInput
              value={section.content.subtitle2 || ''}
              onChange={(subtitle2) =>
                onChange({ ...section, content: { ...section.content, subtitle2 } })
              }
              placeholder="Optional second line, shown under the subtitle"
            />
          </Field>
          <Field label="Subtitle Color">
            <TextInput
              value={section.content.subtitleColor || '#80deea'}
              onChange={(subtitleColor) =>
                onChange({ ...section, content: { ...section.content, subtitleColor } })
              }
            />
          </Field>
          <Field label="Logo URL">
            <TextInput
              value={section.content.logoUrl || ''}
              onChange={(logoUrl) =>
                onChange({ ...section, content: { ...section.content, logoUrl } })
              }
              placeholder="Leave empty for Mirasee logo"
            />
          </Field>
          <Field label="Price Badge Text">
            <TextInput
              value={section.content.priceBadgeText || ''}
              onChange={(priceBadgeText) =>
                onChange({ ...section, content: { ...section.content, priceBadgeText } })
              }
              placeholder={priceBadgePlaceholder || 'e.g., Price: your choice!'}
            />
            {priceBadgePlaceholder && !section.content.priceBadgeText && (
              <span class="text-xs text-gray-500">
                Leave blank to auto-fill from the selected price.
              </span>
            )}
          </Field>
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-gray-700">Gradient Colors</span>
            <ColorInput
              value={section.settings?.gradientFrom || '#0077cc'}
              onChange={(gradientFrom) =>
                onChange({
                  ...section,
                  settings: { ...section.settings, gradientFrom },
                })
              }
              label="From"
            />
            <ColorInput
              value={section.settings?.gradientTo || '#001a6e'}
              onChange={(gradientTo) =>
                onChange({
                  ...section,
                  settings: { ...section.settings, gradientTo },
                })
              }
              label="To"
            />
          </div>
        </div>
      );

    case 'eyebrow':
      return (
        <Field label="Eyebrow Text">
          <TextInput
            value={section.content.text}
            onChange={(text) => onChange({ ...section, content: { text } })}
            placeholder="HERE'S WHAT TO EXPECT"
          />
        </Field>
      );

    case 'callout':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Icon">
            <select
              value={section.content.icon || 'book'}
              onChange={(e) =>
                onChange({
                  ...section,
                  content: {
                    ...section.content,
                    icon: (e.target as HTMLSelectElement).value as
                      | 'book'
                      | 'heart'
                      | 'star'
                      | 'check',
                  },
                })
              }
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="book">Book</option>
              <option value="heart">Heart</option>
              <option value="star">Star</option>
              <option value="check">Check</option>
            </select>
          </Field>
          <Field label="Heading">
            <TextInput
              value={section.content.heading}
              onChange={(heading) =>
                onChange({ ...section, content: { ...section.content, heading } })
              }
            />
          </Field>
          <Field label="Description (optional)">
            <TextArea
              value={section.content.description || ''}
              onChange={(description) =>
                onChange({ ...section, content: { ...section.content, description } })
              }
            />
          </Field>
        </div>
      );

    case 'schedule':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Heading (optional)">
            <TextInput
              value={section.content.heading || ''}
              onChange={(heading) =>
                onChange({ ...section, content: { ...section.content, heading } })
              }
            />
          </Field>
          <span class="text-sm font-medium text-gray-700">Items</span>
          <RepeatableItems
            items={section.content.items}
            onChange={(items) => onChange({ ...section, content: { ...section.content, items } })}
            createItem={() => ({
              label: `Day ${section.content.items.length + 1}`,
              title: '',
              description: '',
            })}
            addLabel="Add item"
            renderItem={(item, _i, update) => (
              <div class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
                <TextInput
                  value={item.label}
                  onChange={(label) => update({ ...item, label })}
                  placeholder="Day 1"
                />
                <TextInput
                  value={item.title}
                  onChange={(title) => update({ ...item, title })}
                  placeholder="Title"
                />
                <TextArea
                  value={item.description}
                  onChange={(description) => update({ ...item, description })}
                  placeholder="Description"
                  rows={2}
                />
              </div>
            )}
          />
        </div>
      );

    case 'content':
      return (
        <div class="flex flex-col gap-4">
          <span class="text-sm font-medium text-gray-700">Content Blocks</span>
          <RepeatableItems
            items={section.content.blocks}
            onChange={(blocks) => onChange({ ...section, content: { ...section.content, blocks } })}
            createItem={() => ({ type: 'paragraph' as const, text: '' })}
            addLabel="Add block"
            renderItem={(block, _i, update) => (
              <div class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
                <select
                  value={block.type}
                  onChange={(e) =>
                    update({
                      ...block,
                      type: (e.target as HTMLSelectElement).value as typeof block.type,
                    })
                  }
                  class="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="paragraph">Paragraph</option>
                  <option value="heading">Heading</option>
                  <option value="blockquote">Blockquote</option>
                  <option value="highlight">Highlight</option>
                  <option value="html">Raw HTML</option>
                </select>
                {block.type === 'html' ? (
                  <TextArea
                    value={block.html || ''}
                    onChange={(html) => update({ ...block, html })}
                    placeholder="<p>HTML content...</p>"
                    rows={4}
                  />
                ) : (
                  <TextArea
                    value={block.text || ''}
                    onChange={(text) => update({ ...block, text })}
                    placeholder="Enter text..."
                    rows={3}
                  />
                )}
              </div>
            )}
          />
        </div>
      );

    case 'valueStack':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Heading (optional)">
            <TextInput
              value={section.content.heading || ''}
              onChange={(heading) =>
                onChange({ ...section, content: { ...section.content, heading } })
              }
            />
          </Field>
          <span class="text-sm font-medium text-gray-700">Items</span>
          <RepeatableItems
            items={section.content.items}
            onChange={(items) => onChange({ ...section, content: { ...section.content, items } })}
            createItem={() => ({ bold: '', text: '' })}
            addLabel="Add item"
            renderItem={(item, _i, update) => (
              <div class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
                <TextInput
                  value={item.bold}
                  onChange={(bold) => update({ ...item, bold })}
                  placeholder="Bold text"
                />
                <TextInput
                  value={item.text || ''}
                  onChange={(text) => update({ ...item, text })}
                  placeholder="Description (optional)"
                />
              </div>
            )}
          />
        </div>
      );

    case 'faq':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Heading">
            <TextInput
              value={section.content.heading || ''}
              onChange={(heading) =>
                onChange({ ...section, content: { ...section.content, heading } })
              }
              placeholder="Frequently Asked Questions"
            />
          </Field>
          <span class="text-sm font-medium text-gray-700">Questions</span>
          <RepeatableItems
            items={section.content.items}
            onChange={(items) => onChange({ ...section, content: { ...section.content, items } })}
            createItem={() => ({ question: '', answer: '' })}
            addLabel="Add question"
            renderItem={(item, _i, update) => (
              <div class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
                <TextInput
                  value={item.question}
                  onChange={(question) => update({ ...item, question })}
                  placeholder="Question"
                />
                <TextArea
                  value={item.answer}
                  onChange={(answer) => update({ ...item, answer })}
                  placeholder="Answer"
                  rows={2}
                />
              </div>
            )}
          />
        </div>
      );

    case 'integratedCheckout':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Pricing Note">
            <TextInput
              value={section.content.pricingNote || ''}
              onChange={(pricingNote) =>
                onChange({ ...section, content: { ...section.content, pricingNote } })
              }
              placeholder="e.g., One-time payment"
            />
          </Field>
          <Field label="Deadline Text">
            <TextInput
              value={section.content.deadlineText || ''}
              onChange={(deadlineText) =>
                onChange({ ...section, content: { ...section.content, deadlineText } })
              }
              placeholder="e.g., Registration closes April 15."
            />
          </Field>
          <Field label="Contact Phone">
            <TextInput
              value={section.content.contactPhone || ''}
              onChange={(contactPhone) =>
                onChange({ ...section, content: { ...section.content, contactPhone } })
              }
              placeholder="(800) 732-0446"
            />
          </Field>
          <Field label="Contact Email">
            <TextInput
              value={section.content.contactEmail || ''}
              onChange={(contactEmail) =>
                onChange({ ...section, content: { ...section.content, contactEmail } })
              }
              placeholder="accounts@mirasee.com"
            />
          </Field>
          <Field label="Button Text">
            <TextInput
              value={section.content.ctaText || ''}
              onChange={(ctaText) =>
                onChange({ ...section, content: { ...section.content, ctaText } })
              }
              placeholder="Complete Purchase →"
            />
          </Field>
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-gray-700">Testimonials</span>
            <RepeatableItems
              items={section.content.testimonials || []}
              onChange={(testimonials) =>
                onChange({ ...section, content: { ...section.content, testimonials } })
              }
              createItem={() => ({ quote: '', name: '', title: '', photoUrl: '' })}
              addLabel="Add testimonial"
              renderItem={(item, _i, update) => (
                <div class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
                  <TextArea
                    value={item.quote}
                    onChange={(quote) => update({ ...item, quote })}
                    placeholder="Quote"
                    rows={2}
                  />
                  <TextInput
                    value={item.name}
                    onChange={(name) => update({ ...item, name })}
                    placeholder="Name"
                  />
                  <TextInput
                    value={item.title || ''}
                    onChange={(title) => update({ ...item, title })}
                    placeholder="Title (optional)"
                  />
                </div>
              )}
            />
          </div>
        </div>
      );

    case 'instructor':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Name">
            <TextInput
              value={section.content.name}
              onChange={(name) => onChange({ ...section, content: { ...section.content, name } })}
            />
          </Field>
          <Field label="Title">
            <TextInput
              value={section.content.title || ''}
              onChange={(title) => onChange({ ...section, content: { ...section.content, title } })}
            />
          </Field>
          <Field label="Photo URL">
            <TextInput
              value={section.content.photoUrl || ''}
              onChange={(photoUrl) =>
                onChange({ ...section, content: { ...section.content, photoUrl } })
              }
            />
          </Field>
          <Field label="Bio">
            <TextArea
              value={section.content.bio}
              onChange={(bio) => onChange({ ...section, content: { ...section.content, bio } })}
              rows={4}
            />
          </Field>
        </div>
      );

    case 'urgency':
      return (
        <Field label="Urgency Text">
          <TextInput
            value={section.content.text}
            onChange={(text) => onChange({ ...section, content: { text } })}
            placeholder="Limited spots available!"
          />
        </Field>
      );

    case 'footer':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Logo URL">
            <TextInput
              value={section.content.logoUrl || ''}
              onChange={(logoUrl) =>
                onChange({ ...section, content: { ...section.content, logoUrl } })
              }
            />
          </Field>
          <Field label="Disclaimer HTML">
            <TextArea
              value={section.content.disclaimerHtml || ''}
              onChange={(disclaimerHtml) =>
                onChange({ ...section, content: { ...section.content, disclaimerHtml } })
              }
              rows={3}
            />
          </Field>
          <Field label="Contact Phone">
            <TextInput
              value={section.content.contactPhone || ''}
              onChange={(contactPhone) =>
                onChange({ ...section, content: { ...section.content, contactPhone } })
              }
            />
          </Field>
          <Field label="Contact Email">
            <TextInput
              value={section.content.contactEmail || ''}
              onChange={(contactEmail) =>
                onChange({ ...section, content: { ...section.content, contactEmail } })
              }
            />
          </Field>
          <Field label="Terms & Conditions HTML (shows as popup link in footer)">
            <TextArea
              value={section.content.termsHtml || ''}
              onChange={(termsHtml) =>
                onChange({ ...section, content: { ...section.content, termsHtml } })
              }
              rows={6}
            />
          </Field>
        </div>
      );

    case 'video':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Video URL">
            <TextInput
              value={section.content.url}
              onChange={(url) => onChange({ ...section, content: { ...section.content, url } })}
              placeholder="https://vimeo.com/... or https://youtube.com/..."
            />
          </Field>
          <Field label="Caption (optional)">
            <TextInput
              value={section.content.caption || ''}
              onChange={(caption) =>
                onChange({ ...section, content: { ...section.content, caption } })
              }
            />
          </Field>
        </div>
      );

    case 'testimonials':
      return (
        <div class="flex flex-col gap-4">
          <span class="text-sm font-medium text-gray-700">Testimonials</span>
          <RepeatableItems
            items={section.content.items}
            onChange={(items) => onChange({ ...section, content: { ...section.content, items } })}
            createItem={() => ({ quote: '', name: '', title: '', photoUrl: '' })}
            addLabel="Add testimonial"
            renderItem={(item, _i, update) => (
              <div class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
                <TextArea
                  value={item.quote}
                  onChange={(quote) => update({ ...item, quote })}
                  placeholder="Quote"
                  rows={2}
                />
                <TextInput
                  value={item.name}
                  onChange={(name) => update({ ...item, name })}
                  placeholder="Name"
                />
                <TextInput
                  value={item.title || ''}
                  onChange={(title) => update({ ...item, title })}
                  placeholder="Title (optional)"
                />
              </div>
            )}
          />
        </div>
      );

    case 'trust':
      return (
        <div class="flex flex-col gap-4">
          <Field label="Guarantee Text">
            <TextInput
              value={section.content.guaranteeText || ''}
              onChange={(guaranteeText) =>
                onChange({ ...section, content: { ...section.content, guaranteeText } })
              }
            />
          </Field>
          <Field label="Phone">
            <TextInput
              value={section.content.phone || ''}
              onChange={(phone) => onChange({ ...section, content: { ...section.content, phone } })}
            />
          </Field>
        </div>
      );

    case 'cta':
      return (
        <div class="flex flex-col gap-4">
          {!section.content.acceptText && (
            <div class="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <IcoExclamation class="size-3.5 flex-shrink-0" />
              Accept button text is required.
            </div>
          )}
          <Field label="Accept Button Text">
            <TextInput
              value={section.content.acceptText}
              onChange={(acceptText) =>
                onChange({ ...section, content: { ...section.content, acceptText } })
              }
              placeholder="Yes, add this!"
            />
          </Field>
          <Field label="Decline Button Text">
            <TextInput
              value={section.content.declineText}
              onChange={(declineText) =>
                onChange({ ...section, content: { ...section.content, declineText } })
              }
              placeholder="No thanks, continue"
            />
          </Field>
        </div>
      );

    default:
      return (
        <p class="text-sm text-gray-500">
          No editor available for this section type ({section.type}).
        </p>
      );
  }
}
